import axios from 'axios';
import API_CONFIG from '../config/api';

axios.defaults.baseURL = API_CONFIG.baseURL;

function currentToken() {
  try {
    return localStorage.getItem('token') || '';
  } catch (e) {
    return '';
  }
}

function authHeaders(extra = {}) {
  const token = currentToken();
  const headers = { ...extra };
  if (token) {
    headers.Authorization = `Token ${token}`;
    headers['X-Auth-Token'] = token;
  }
  return headers;
}

axios.interceptors.request.use((config) => {
  config.headers = authHeaders(config.headers || {});
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    if (status === 401) {
      try {
        localStorage.removeItem('token');
      } catch (e) {
        /* ignore */
      }
      if (typeof window !== 'undefined' && !window.location.pathname.includes('/admin/login')) {
        window.location.replace('/admin/login');
      }
    }
    return Promise.reject(error);
  }
);

class API {
  constructor()
  {
    this.endpoints = {};
    this.endpoints.user = this.createBasicCRUDEndpoints({ name: 'user' });
    this.endpoints.posts = this.createBasicCRUDEndpoints({ name: 'posts' });
    this.endpoints.services = this.createBasicCRUDEndpoints({ name: 'services' });
    this.endpoints.employees = this.createBasicCRUDEndpoints({ name: 'employees' });
    this.endpoints.site = this.createBasicCRUDEndpoints({ name: 'site' });
    this.endpoints.site.update = (toUpdate, config = {}) => {
      const mergedConfig = {
        ...config,
        headers: authHeaders(config.headers),
      };
      return axios.put(`v3/admin/site/`, toUpdate, mergedConfig).catch(err => console.log('error'));
    };
    this.endpoints.image = this.createBasicCRUDEndpoints({ name: 'image' });
    this.endpoints.image.uploadStatus = (config = {}) => axios.get('v3/admin/image/upload-status/', {
      ...config,
      headers: authHeaders(config.headers),
    });

    this.endpoints.posts.addImages = (postId, imageIds, order = null, config = {}) => {
      const mergedConfig = {
        ...config,
        headers: authHeaders({
          ...config.headers,
          'Content-Type': 'application/json',
        }),
      };
      const body = Array.isArray(imageIds)
        ? { image_id: imageIds, ...(order !== null && { order }) }
        : { image_id: imageIds, ...(order !== null && { order }) };
      return axios.post(`v3/admin/posts/${postId}/images`, body, mergedConfig);
    };

    this.endpoints.posts.getImages = (postId, config = {}) => axios.get(`v3/admin/posts/${postId}/images`, {
      ...config,
      headers: authHeaders(config.headers),
    });

    this.endpoints.posts.replaceImages = (postId, imageIds, config = {}) => axios.put(`v3/admin/posts/${postId}/images`, {
      images: imageIds.map((id, index) => ({ id, order: index })),
    }, {
      ...config,
      headers: authHeaders({
        ...config.headers,
        'Content-Type': 'application/json',
      }),
    });

    this.endpoints.posts.removeImage = (postId, imageId, config = {}) => axios.delete(
      `v3/admin/posts/${postId}/images/${imageId}`,
      { ...config, headers: authHeaders(config.headers) }
    );
    this.endpoints.appointmentServices = this.createBasicCRUDEndpoints({ name: 'appointment/services' });
    this.endpoints.appointmentChecks = this.createBasicCRUDEndpoints({ name: 'appointment/checks' });
    this.endpoints.formAppointments = this.createBasicCRUDEndpoints({ name: 'form/appointment' });
    this.endpoints.formContacts = this.createBasicCRUDEndpoints({ name: 'form/contact' });
    this.endpoints.gallery = this.createBasicCRUDEndpoints({ name: 'gallery' });

    this.endpoints.gallery.addImages = (galleryId, imageIds, order = null, config = {}) => {
      const mergedConfig = {
        ...config,
        headers: authHeaders({
          ...config.headers,
          'Content-Type': 'application/json',
        }),
      };
      const body = Array.isArray(imageIds)
        ? { image_id: imageIds, ...(order !== null && { order: order }) }
        : { image_id: imageIds, ...(order !== null && { order: order }) };

      return axios.post(`v3/admin/gallery/${galleryId}/images`, body, mergedConfig)
        .then(response => response)
        .catch(err => {
          console.error('Error adding images to gallery:', err);
          throw err;
        });
    };

    this.endpoints.gallery.getImages = (galleryId, config = {}) => {
      const mergedConfig = {
        ...config,
        headers: authHeaders(config.headers),
      };
      return axios.get(`v3/admin/gallery/${galleryId}/images`, mergedConfig)
        .catch(err => {
          console.error('Error in getImages:', err);
          throw err;
        });
    };

    this.endpoints.gallery.removeImage = (galleryId, imageId, config = {}) => {
      const mergedConfig = {
        ...config,
        headers: authHeaders(config.headers),
      };
      return axios.delete(`v3/admin/gallery/${galleryId}/images/${imageId}`, mergedConfig).catch(err => console.log('error'));
    };

    this.openEndpoints = {};
    this.openEndpoints.site = this.createBasicCRUDOpenEndpoints({ name: 'site' });
    this.openEndpoints.services = this.createBasicCRUDOpenEndpoints({ name: 'services' });
    this.openEndpoints.posts = this.createBasicCRUDOpenEndpoints({ name: 'posts' });
    this.openEndpoints.postsImages = this.createBasicCRUDOpenEndpoints({ name: 'posts/images' });
    this.openEndpoints.employees = this.createBasicCRUDOpenEndpoints({ name: 'employees' });
    this.openEndpoints.appointmentServices = this.createBasicCRUDOpenEndpoints({ name: 'appointment/services' });
    this.openEndpoints.appointmentChecks = this.createBasicCRUDOpenEndpoints({ name: 'appointment/checks' });
    this.openEndpoints.formAppointment = this.createBasicCRUDOpenEndpoints({ name: 'form/appointment' });
    this.openEndpoints.formContact = this.createBasicCRUDOpenEndpoints({ name: 'form/contact' });
    this.openEndpoints.gallery = this.createBasicCRUDOpenEndpoints({ name: 'gallery' });

    this.openEndpoints.gallery.getLatestImages = (limit = 20, offset = 0, config = {}) => {
      const mergedConfig = {
        ...config,
        params: {
          limit: limit,
          offset: offset
        }
      };
      return axios.get(`v3/gallery/latest-images`, mergedConfig).catch(err => console.log('error'));
    };
  }

  createBasicCRUDOpenEndpoints = ({ name }) => {
    const openEndpoints = {};
    const resourceURL = `v3/${name}`;

    openEndpoints.getAll = (config = { crossDomain: true }) => axios.get(`${resourceURL}/`, config);
    openEndpoints.getPage = ({ get }, config = { crossDomain: true, params: get}) => axios.get(`${resourceURL}/`, config);
    openEndpoints.getOne = ({ id }, config = { crossDomain: true }) => axios.get(`${resourceURL}/${id}/`, config);
    openEndpoints.create = (toCreate, config = { crossDomain: true }) => axios.post(`${resourceURL}/`, toCreate, config);

    return openEndpoints;
  }

  createBasicCRUDEndpoints = ({ name }) => {
    const endpoints = {};
    const resourceURL = `v3/admin/${name}`;

    endpoints.getAll = (config = {}) => axios.get(`${resourceURL}/`, {
      ...config,
      headers: authHeaders(config.headers),
    });
    endpoints.getOne = ({ id }, config = {}) => axios.get(`${resourceURL}/${id}/`, {
      ...config,
      headers: authHeaders(config.headers),
    });
    endpoints.getPage = ({ get }, config = {}) => axios.get(`${resourceURL}/`, {
      ...config,
      params: get,
      headers: authHeaders(config.headers),
    });
    endpoints.create = (toCreate, config = {}) => {
      const isFormData = typeof FormData !== 'undefined' && toCreate instanceof FormData;
      return axios.post(`${resourceURL}/`, toCreate, {
        ...config,
        timeout: config.timeout || (isFormData ? 120000 : undefined),
        maxContentLength: config.maxContentLength ?? (isFormData ? Infinity : undefined),
        maxBodyLength: config.maxBodyLength ?? (isFormData ? Infinity : undefined),
        headers: authHeaders(config.headers),
      });
    };
    endpoints.update = (toUpdate, config = {}) => axios.put(`${resourceURL}/${toUpdate.id}/`, toUpdate, {
      ...config,
      headers: authHeaders(config.headers),
    });
    endpoints.patch = ({ id }, toPatch, config = {}) => axios.patch(`${resourceURL}/${id}/`, toPatch, {
      ...config,
      headers: authHeaders(config.headers),
    });
    endpoints.delete = ({ id }, config = {}) => axios.delete(`${resourceURL}/${id}/`, {
      ...config,
      headers: authHeaders(config.headers),
    });

    return endpoints;
  }
}

export default API;
